import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Sparkles, 
  Trash2, 
  Loader2, 
  Calendar,
  MessageCircle,
  Send,
  Mic,
  Square
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getDream, analyzeDream, deleteDream, getDreamContext, continueDreamConversation, getDreamConversation, transcribeAudio } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

const MOOD_COLORS = {
  peaceful: 'bg-blue-500/20 text-blue-400',
  anxious: 'bg-amber-500/20 text-amber-400',
  confused: 'bg-violet-500/20 text-violet-400',
  frightened: 'bg-red-500/20 text-red-400',
  excited: 'bg-emerald-500/20 text-emerald-400',
  sad: 'bg-gray-500/20 text-gray-400',
  relief: 'bg-cyan-500/20 text-cyan-400',
  neutral: 'bg-gray-500/20 text-gray-400',
};

export default function DreamDetail() {
  const { dreamId } = useParams();
  const navigate = useNavigate();
  const [dream, setDream] = useState(null);
  const [context, setContext] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Conversation state
  const [conversation, setConversation] = useState(null);
  const [showConversation, setShowConversation] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  
  // Voice recording state for conversation
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isTranscribingVoice, setIsTranscribingVoice] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  useEffect(() => {
    const fetchDream = async () => {
      try {
        const response = await getDream(dreamId);
        setDream(response.data);
        
        // Fetch conversation if dream has analysis
        if (response.data.analysis) {
          try {
            const convResponse = await getDreamConversation(dreamId);
            setConversation(convResponse.data);
          } catch {
            // No conversation yet
          }
        }
        
        // Quietly fetch contextual intelligence in background
        // This should feel seamless, not like a separate feature
        try {
          const contextResponse = await getDreamContext(dreamId);
          setContext(contextResponse.data);
        } catch {
          // Silently fail - context is enhancement, not requirement
        }
      } catch (error) {
        console.error('Failed to fetch dream:', error);
        toast.error('Dream not found');
        navigate('/journal');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDream();
    
    // Cleanup voice recording on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [dreamId, navigate]);
  
  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      const response = await analyzeDream(dreamId);
      setDream(prev => ({
        ...prev,
        analysis: response.data.analysis,
        symbols: response.data.symbols,
        essence: response.data.essence,
        analyzed_at: new Date().toISOString(),
      }));
      
      // Quietly refresh context after analysis
      try {
        const contextResponse = await getDreamContext(dreamId);
        setContext(contextResponse.data);
      } catch {
        // Silently fail
      }
      
      toast.success('Analysis complete');
    } catch (error) {
      console.error('Analysis failed:', error);
      
      // Check if it's an analysis limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'analysis_limit_reached') {
        toast.error(
          <div className="flex flex-col gap-2">
            <span>Monthly analysis limit reached</span>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/subscription')}
            >
              Upgrade for unlimited
            </Button>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error('Analysis failed. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteDream(dreamId);
      toast.success('Dream deleted');
      navigate('/journal');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete dream');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleSendMessage = async () => {
    if (!userMessage.trim() || isSendingMessage) return;
    
    setIsSendingMessage(true);
    try {
      const response = await continueDreamConversation(dreamId, userMessage);
      
      // Update conversation state
      const updatedConversation = {
        exchanges: [
          ...(conversation?.exchanges || []),
          {
            exchange_number: response.data.exchange_count,
            user_message: userMessage,
            ai_response: response.data.response,
            timestamp: new Date().toISOString()
          }
        ],
        exchange_count: response.data.exchange_count,
        is_complete: response.data.is_complete
      };
      
      setConversation(updatedConversation);
      setUserMessage('');
      
      if (response.data.is_complete) {
        toast.success('Analysis complete!');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };
  
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await handleVoiceTranscription(audioBlob);
        }
      };
      
      mediaRecorder.start(1000);
      setIsRecordingVoice(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };
  
  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecordingVoice) {
      mediaRecorderRef.current.stop();
      setIsRecordingVoice(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };
  
  const handleVoiceTranscription = async (audioBlob) => {
    setIsTranscribingVoice(true);
    try {
      const response = await transcribeAudio(audioBlob);
      const transcribedText = response.data.text;
      
      // Add to existing message or replace
      setUserMessage(prev => prev ? `${prev} ${transcribedText}` : transcribedText);
      toast.success('Voice transcribed');
    } catch (error) {
      console.error('Transcription failed:', error);
      toast.error('Could not transcribe audio. Please try again.');
    } finally {
      setIsTranscribingVoice(false);
    }
  };
  
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!dream) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="dream-detail-page">
      {/* Hero Background */}
      <div 
        className="absolute inset-x-0 top-0 h-64 opacity-30"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1517444175997-8f00340995bc?w=800&q=60)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
        }}
      />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-destructive hover:text-destructive"
                data-testid="delete-dream-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this dream?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your dream and its analysis.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </header>
        
        {/* Dream Content */}
        <div className="px-6">
          {/* Meta info */}
          <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span className="font-mono">{format(new Date(dream.created_at), 'MMMM d, yyyy')}</span>
            </div>
            
            {dream.mood && (
              <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${MOOD_COLORS[dream.mood]}`}>
                {dream.mood}
              </span>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-serif font-bold mb-4 leading-tight">
            {dream.title}
          </h1>
          
          {/* Dream Essence - Subtle highlighted line */}
          {dream.essence && (
            <div className="mb-6 border-l-2 border-primary/30 pl-4">
              <p className="text-sm italic text-muted-foreground/80">
                {dream.essence}
              </p>
            </div>
          )}
          
          {/* Dream narrative */}
          <div className="glass-card p-6 mb-6">
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              {dream.content}
            </p>
          </div>
          
          {/* Symbols with quiet insights */}
          {dream.symbols?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">
                Symbols Present
              </h2>
              <div className="flex flex-wrap gap-2 mb-3">
                {dream.symbols.map((symbol, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-primary/20 text-primary text-sm capitalize"
                  >
                    {symbol}
                  </span>
                ))}
              </div>
              
              {/* Symbol insights - shown subtly when available */}
              {context?.symbol_insights?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {context.symbol_insights.map((sym, idx) => (
                    <p key={idx} className="text-xs text-muted-foreground/70 italic pl-2 border-l border-primary/20">
                      <span className="capitalize">{sym.symbol}</span>: {sym.insight}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Analysis Section */}
          {dream.analysis ? (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h2 className="font-serif font-bold text-xl">Reflection</h2>
              </div>
              
              <div className="glass-card p-6">
                <div className="prose prose-invert max-w-none">
                  <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                    {dream.analysis}
                  </p>
                </div>
                
                {dream.analyzed_at && (
                  <p className="text-xs text-muted-foreground/50 mt-4">
                    {format(new Date(dream.analyzed_at), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
              
              {/* Evolution hint - appears occasionally, not every dream */}
              {context?.evolution_hint && (
                <div className="mt-6 py-4 border-t border-border/30">
                  <p className="text-xs text-muted-foreground/60 italic leading-relaxed">
                    <span className="text-primary/50">A pattern noticed over time:</span>{' '}
                    {context.evolution_hint.insight}
                  </p>
                </div>
              )}
              
              {/* Conversation Feature */}
              {!conversation?.is_complete && conversation?.exchange_count < 3 && (
                <div className="mt-6">
                  {!showConversation ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowConversation(true)}
                      className="w-full"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Continue the conversation
                    </Button>
                  ) : null}
                </div>
              )}
              
              {/* Conversation Thread */}
              {(showConversation || conversation?.exchanges?.length > 0) && (
                <div className="mt-6 glass-card p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageCircle className="w-4 h-4 text-primary" />
                    <h3 className="font-medium text-sm">Conversation</h3>
                    {conversation?.is_complete && (
                      <span className="ml-auto text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded-full">
                        Complete
                      </span>
                    )}
                  </div>
                  
                  {/* Exchange History */}
                  {conversation?.exchanges?.map((exchange, idx) => (
                    <div key={idx} className="space-y-3">
                      {/* User Message */}
                      <div className="flex justify-end">
                        <div className="max-w-[80%] bg-primary/10 rounded-lg p-3">
                          <p className="text-sm text-foreground">{exchange.user_message}</p>
                        </div>
                      </div>
                      
                      {/* AI Response */}
                      <div className="flex justify-start">
                        <div className="max-w-[85%] bg-muted/50 rounded-lg p-3">
                          <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
                            {exchange.ai_response}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Input Area */}
                  {!conversation?.is_complete && (
                    <div className="pt-4 border-t border-border/30">
                      <div className="space-y-2">
                        <Textarea
                          value={userMessage}
                          onChange={(e) => setUserMessage(e.target.value)}
                          placeholder="Share your thoughts or answer the question..."
                          className="min-h-[80px] resize-none"
                          disabled={isSendingMessage || isRecordingVoice || isTranscribingVoice}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        
                        {/* Voice Recording Status */}
                        {isRecordingVoice && (
                          <div className="flex items-center gap-2 text-sm text-primary">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            Recording... {formatRecordingTime(recordingTime)}
                          </div>
                        )}
                        
                        {isTranscribingVoice && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Transcribing...
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {conversation?.exchange_count || 0} / 3 exchanges
                          </span>
                          
                          {/* Voice Recording Button */}
                          {!isRecordingVoice ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={startVoiceRecording}
                              disabled={isSendingMessage || isTranscribingVoice}
                              className="h-8 w-8 p-0"
                              title="Record voice response"
                            >
                              <Mic className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={stopVoiceRecording}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                              title="Stop recording"
                            >
                              <Square className="w-4 h-4 fill-current" />
                            </Button>
                          )}
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={handleSendMessage}
                          disabled={!userMessage.trim() || isSendingMessage || isRecordingVoice || isTranscribingVoice}
                          className="rounded-full"
                        >
                          {isSendingMessage ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {conversation?.is_complete && (
                    <p className="text-xs text-center text-muted-foreground italic pt-2">
                      Analysis complete. This conversation has reached its natural conclusion.
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="glass-card p-8 text-center">
              <img src={ONIRVA_ICON} alt="" className="w-16 h-16 mx-auto mb-4 opacity-50 rounded-xl" />
              <h3 className="font-serif font-bold mb-2">Ready when you are</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Explore this dream further
              </p>
              
              <Button
                className="rounded-full glow-sm"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reflecting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Reflect on Dream
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
